import PropTypes from 'prop-types';
import React, { Component } from 'react';
import classNames from 'classnames';

import { radioButtonGroupUIComponent } from 'ctm-css-themes';
import { get, sanitizeInnerHtml, applyVerticalStyles } from 'ctm-util-functions';

import { getCheckboxDisabledProp, getRadioComponentStateSuffix, getDisabledProp } from '../utils';
import * as actions from '../actions';

class RadioButtonGroup extends Component {

    /**
     * Default the focused state to not focused.
     * @param {Object} props The component properties
     */
    constructor(props) {
        super(props);
        this.state = {
            focus: false
        };
        this.styles = applyVerticalStyles(radioButtonGroupUIComponent, 'radio_button');
    }

    /**
     * If the input should be checked by default, set the value for the state during
     * onMount and the input will appear checked
     */
    componentDidMount = () => {
        const { optionProps, dispatch, schema } = this.props;
        const elementProps = optionProps;
        if (elementProps.checked) {
            dispatch(
                actions.setFormValue(
                    schema.identifier,
                    elementProps.value,
                    schema.stateKey,
                    true
                )
            );
        }
    };

    /**
     * On updates, check if this input is disabled, if it is, dispatch an action to ensure that it's value is not
     * set on state for the input's identifier.
     * @todo: identify cases where we need this to happen and review if this is still needed, seems like a hack, we should be more explicit about resetting values
     */
    componentDidUpdate = () => {
        const { dispatch, schema, value } = this.props;
        if (getCheckboxDisabledProp(this.props).disabled === true) {
            dispatch(
                actions.resetValueIfSet(
                    schema.identifier,
                    value,
                    schema.stateKey
                )
            );
        }
    };

    /**
     * Call the setValue function when the value of the input has been changed
     * by the user.
     * @param {Object} event - event object
     */
    onChange = (event) => {
        const { schema, dispatch } = this.props;
        // @todo: temp hack until we can sort out componentDidUpdate - this is only set on the movingIn question on energy E2E in the application step
        if (schema.disableChanges) {
            return;
        }
        event.persist();

        // if checked, set the value, else, clear the value
        let value = '';
        let isChecked = 'unchecked';
        if (event.target.checked) {
            value = event.target.value;
            isChecked = 'checked';
        }
        dispatch(
            actions.setFormValue(
                schema.backendIdentifier || schema.identifier,
                value,
                schema.stateKey,
                true
            )
        );
        // This is only required in like checkbox or radio button groupy thing -- check schema
        const eventProps = schema.eventProps;
        const onChangeActions = schema.onChangeActions;
        if (eventProps && eventProps.onChange) {
            if (eventProps.onChange[isChecked]) {
                const eventActions = eventProps.onChange[isChecked].actions;
                for (const action of eventActions) {
                    dispatch(actions[action.type](action));
                }
            }
        } else if (Array.isArray(onChangeActions)) {
            onChangeActions.map((action) => dispatch(action));
        }
    };

    /**
     *
     * Handler for the onClick Event
     * @param  {Object} event - the onClick event object
     * @returns {Boolean} Always return true to allow events to bubble
     */
    onClick = (event) => {
        // NOTE: This is required because badges and radio button groups can be selected
        // to trigger the next step to load. If you go forward, then back a step, and select
        // the same value, prior to React DOM 15.6.0, it triggered the onChange event.
        // Now we're on React 16, it doesn't trigger subsequent onChange events, so we had to hack this in.
        // @WARN I don't know what this will do if multiple actions are dispatched on the onChange!!!!
        // But it should just be what it was before we upgraded anyway.
        const { value, dispatch } = this.props;
        if (value === event.target.value) {
            this.onChange(event);
        }
        const { eventProps } = this.props;
        if (!eventProps) {
            return true;
        }

        const clickMethod = eventProps.onClickFunction || eventProps.onClick;
        if (clickMethod) {
            clickMethod(event);
        } else if (eventProps.onClickActions) {
            eventProps.onClickActions.map((action) => dispatch(action));
        }
        return true;
    };

    /**
     * We need to apply focused states to container divs, can't use :focus pseudo selector,
     * so we need to re-render the component.
     * @param {Boolean} state True if focusing, False is blurring.
     */
    toggleFocus = (state) => {
        this.setState({
            focus: state
        });
    };

    /**
     * Get the label for the checkbox.
     * @param  {Object} elementProps - The content props.
     * @param  {String} elementId - The element Id
     * @returns {String} The value
     */
    getContent = (elementProps, elementId) => {
        const label = [];
        if (elementProps.label) {
            label.push(<span key={`${elementId}.label`}>{elementProps.label}</span>);
        } else if (elementProps.innerHtml) {
            // eslint-disable-next-line react/no-danger
            label.push(<span dangerouslySetInnerHTML={{ __html: sanitizeInnerHtml(elementProps.innerHtml) }} key={`${elementId}.innerHtml`} />);
        }
        return label;
    };

    /**
     * Render a single checkbox and a label.
     * @returns {Node} A rendered badge component in a container.
     */
    render = () => {
        const { schema, optionProps, value } = this.props;
        const { focus } = this.state;
        const elementProps = optionProps;
        const elementValue = elementProps.value;
        const elementId = `${schema.identifier}.${elementValue}`;
        const numberOfItemsInGroup = get(schema, 'field.values', []).length;
        const isChecked = value === elementProps.value;
        const disabledProps = getDisabledProp(this.props);

        // UI Component
        const UIComponentPrefix = schema.field.UIComponent;
        const UIComponent = `${UIComponentPrefix}${getRadioComponentStateSuffix(this.props)}`;
        const minWidthClass = `min_width_${numberOfItemsInGroup}`;
        const focusClass = focus ? 'state_set_focused' : null;
        const componentClass = classNames(this.styles[UIComponent],
            this.styles[minWidthClass], this.styles[focusClass]);

        return (
            <label
                htmlFor={elementId}
                className={componentClass}
                onFocus={() => this.toggleFocus(true)}
                onBlur={() => this.toggleFocus(false)}>
                <input
                    type="radio"
                    checked={isChecked}
                    onChange={this.onChange}
                    onClick={this.onClick}
                    {...getCheckboxDisabledProp(this.props)}
                    name={schema.identifier}
                    value={elementValue}
                    id={elementId}
                    data-id={elementId}
                    {...disabledProps}
                />
                {this.getContent(elementProps, elementId)}
            </label>
        );
    };

}

RadioButtonGroup.propTypes = {
    schema: PropTypes.object.isRequired, // The schema for this form element
    optionProps: PropTypes.object,
    eventProps: PropTypes.object,
    value: PropTypes.string.isRequired,
    dispatch: PropTypes.func.isRequired
};

export { RadioButtonGroup };
